import { Filter } from "lucide-react";
import { Suspense, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "~/components/ui";
import { useDataImportProjectList } from "~/features/dataImportProjects/hooks/useDataImportProjectList";
import type { DipColumnFilterValues } from "~/features/dataImportProjects/types";
import { toDataImportProjectListRows } from "~/features/dataImportProjects/utils/transformers";
import { DataImportProjectColumnFilters } from "./DataImportProjectColumnFilters";

interface DataImportProjectColumnFiltersConnectedProps {
  value: DipColumnFilterValues;
  excludeDeactivated: boolean;
  onApply: (next: DipColumnFilterValues, excludeDeactivated: boolean) => void;
}

// The status filter offers only the statuses present in the loaded projects, so this wrapper
// reads the same (Apollo-cached) list query the table fetches — it shares the cache, no extra request.
function ColumnFiltersWithData({ value, excludeDeactivated, onApply }: DataImportProjectColumnFiltersConnectedProps) {
  const { data } = useDataImportProjectList();

  const statusOptions = useMemo(() => {
    const rows = toDataImportProjectListRows(data.dataImportProjects);
    const scoped = excludeDeactivated ? rows.filter((row) => !row.isDeactivated) : rows;
    return Array.from(new Set(scoped.map((row) => row.statusId))).sort();
  }, [data.dataImportProjects, excludeDeactivated]);

  return (
    <DataImportProjectColumnFilters
      value={value}
      statusOptions={statusOptions}
      excludeDeactivated={excludeDeactivated}
      onApply={onApply}
    />
  );
}

// Lives in the header toolbar next to search, so it renders outside the table's Suspense boundary
// and provides its own — the fallback keeps the button's footprint while the list loads.
export function DataImportProjectColumnFiltersConnected(props: DataImportProjectColumnFiltersConnectedProps) {
  const { t } = useTranslation();

  return (
    <Suspense
      fallback={
        <Button type="button" variant="outline" size="icon" aria-label={t("ui.filter")} disabled>
          <Filter />
        </Button>
      }
    >
      <ColumnFiltersWithData {...props} />
    </Suspense>
  );
}
